import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import * as api from "@/lib/api";
import ChannelSidebar from "@/components/studio/ChannelSidebar";
import ContentPanel from "@/components/studio/ContentPanel";
import TitlePanel from "@/components/studio/TitlePanel";
import TopBar from "@/components/studio/TopBar";
import PromptSettingsDialog from "@/components/studio/PromptSettingsDialog";
import ApiKeysDialog from "@/components/studio/ApiKeysDialog";
import ScenesModal from "@/components/studio/ScenesModal";

const settingsClient = axios.create({ baseURL: api.API });

export default function Studio() {
  const [channels, setChannels] = useState([]);
  const [selectedChannelId, setSelectedChannelId] = useState(null);
  const [articles, setArticles] = useState([]);
  const [titles, setTitles] = useState([]);
  const [activeArticle, setActiveArticle] = useState(null);
  const [loadingTitles, setLoadingTitles] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const [apiKeysOpen, setApiKeysOpen] = useState(false);
  const [scenesArticleId, setScenesArticleId] = useState(null);
  const [hasKeys, setHasKeys] = useState(false);

  const selectedChannel = channels.find((c) => c.id === selectedChannelId) || null;

  const refreshChannels = useCallback(async () => {
    const data = await api.listChannels();
    setChannels(data);
    return data;
  }, []);

  const refreshTitles = useCallback(async (channelId) => {
    if (!channelId) return setTitles([]);
    const t = await api.listTitles(channelId);
    setTitles(t);
  }, []);

  const refreshArticles = useCallback(async (channelId) => {
    if (!channelId) return setArticles([]);
    const a = await api.listArticles(channelId);
    setArticles(a);
  }, []);

  useEffect(() => {
    refreshChannels().catch(() => toast.error("Kanallar yüklenemedi"));
    // Also check API keys state
    settingsClient
      .get("/settings")
      .then((r) => {
        const s = r.data;
        setHasKeys(s.google_api_key_set || s.openai_api_key_set);
        if (!s.google_api_key_set && !s.openai_api_key_set) {
          // Auto-open on first load to prompt user
          setApiKeysOpen(true);
        }
      })
      .catch(() => {});
  }, [refreshChannels]);

  useEffect(() => {
    setActiveArticle(null);
    setScenesArticleId(null);
    if (selectedChannelId) {
      Promise.all([refreshTitles(selectedChannelId), refreshArticles(selectedChannelId)]).catch(
        () => toast.error("İçerikler yüklenemedi"),
      );
    } else {
      setTitles([]);
      setArticles([]);
    }
  }, [selectedChannelId, refreshTitles, refreshArticles]);

  useEffect(() => {
    if (!selectedChannelId && channels.length > 0) {
      setSelectedChannelId(channels[0].id);
    }
  }, [channels, selectedChannelId]);

  // ---- Channels ----
  const handleCreateChannel = async (payload) => {
    try {
      const ch = await api.createChannel(payload);
      setChannels((prev) => [ch, ...prev]);
      setSelectedChannelId(ch.id);
      toast.success("Kanal oluşturuldu");
    } catch {
      toast.error("Kanal oluşturulamadı");
    }
  };

  const handleDeleteChannel = async (id) => {
    try {
      await api.deleteChannel(id);
      const remaining = channels.filter((c) => c.id !== id);
      setChannels(remaining);
      if (selectedChannelId === id) setSelectedChannelId(remaining[0]?.id || null);
      toast.success("Kanal silindi");
    } catch {
      toast.error("Kanal silinemedi");
    }
  };

  // ---- Titles ----
  const handleGenerateTitles = async () => {
    if (!selectedChannel) return;
    setLoadingTitles(true);
    try {
      const newTitles = await api.generateTitles(selectedChannel.id);
      setTitles((prev) => [...newTitles, ...prev]);
      toast.success(`${newTitles.length} yeni başlık üretildi`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Başlık üretimi başarısız");
    } finally {
      setLoadingTitles(false);
    }
  };

  const handleUpdateTitle = async (id, text) => {
    try {
      const updated = await api.updateTitle(id, text);
      setTitles((prev) => prev.map((t) => (t.id === id ? updated : t)));
      toast.success("Başlık güncellendi");
    } catch {
      toast.error("Başlık güncellenemedi");
    }
  };

  const handleDeleteTitle = async (id) => {
    try {
      await api.deleteTitle(id);
      setTitles((prev) => prev.filter((t) => t.id !== id));
      toast.success("Başlık silindi");
    } catch {
      toast.error("Başlık silinemedi");
    }
  };

  // ---- Articles ----
  const handleStartArticleFromTitle = async (title) => {
    // Immediately create the article so the wizard has an ID for AI calls.
    try {
      const created = await api.createArticle({
        channel_id: selectedChannel.id,
        title: title.text,
        title_tr: title.text_tr || "",
        title_id: title.id,
        content: "",
      });
      setArticles((prev) => [created, ...prev]);
      setActiveArticle(created);
      // refresh titles to reflect is_used
      const t = await api.listTitles(selectedChannel.id);
      setTitles(t);
    } catch {
      toast.error("Makale oluşturulamadı");
    }
  };

  const handleOpenArticle = (article) => setActiveArticle(article);

  const handleNewBlankArticle = async () => {
    if (!selectedChannel) return;
    try {
      const created = await api.createArticle({
        channel_id: selectedChannel.id,
        title: "Başlıksız makale",
        title_tr: "",
        content: "",
      });
      setArticles((prev) => [created, ...prev]);
      setActiveArticle(created);
    } catch {
      toast.error("Makale oluşturulamadı");
    }
  };

  const handleSaveMeta = async (patch) => {
    if (!activeArticle?.id) return;
    const updated = await api.updateArticle(activeArticle.id, patch);
    setArticles((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
    setActiveArticle(updated);
    return updated;
  };

  const handleArticleUpdated = (updated) => {
    setActiveArticle(updated);
    setArticles((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
  };

  const handleDeleteArticle = async (id) => {
    try {
      await api.deleteArticle(id);
      setArticles((prev) => prev.filter((a) => a.id !== id));
      if (activeArticle?.id === id) setActiveArticle(null);
      if (scenesArticleId === id) setScenesArticleId(null);
      toast.success("Makale silindi");
    } catch {
      toast.error("Makale silinemedi");
    }
  };

  const handleStartProcessing = (articleId) => {
    setScenesArticleId(articleId);
  };

  const handleScenesUpdate = (updated) => {
    setArticles((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
    if (activeArticle?.id === updated.id) setActiveArticle(updated);
  };

  return (
    <div className="h-screen w-screen flex flex-col bg-background text-foreground grain overflow-hidden">
      <TopBar
        channelsCount={channels.length}
        onOpenPromptSettings={() => setPromptOpen(true)}
        onOpenApiKeys={() => setApiKeysOpen(true)}
        hasKeys={hasKeys}
      />
      <div className="grid grid-cols-12 flex-1 overflow-hidden">
        <div className="col-span-12 md:col-span-3 border-r border-border/60 h-full overflow-hidden">
          <ChannelSidebar
            channels={channels}
            selectedId={selectedChannelId}
            onSelect={setSelectedChannelId}
            onCreate={handleCreateChannel}
            onDelete={handleDeleteChannel}
          />
        </div>
        <div className="hidden md:block md:col-span-6 border-r border-border/60 h-full overflow-hidden">
          <ContentPanel
            channel={selectedChannel}
            articles={articles}
            activeArticle={activeArticle}
            onOpenArticle={handleOpenArticle}
            onNewBlank={handleNewBlankArticle}
            onSaveMeta={handleSaveMeta}
            onArticleUpdated={handleArticleUpdated}
            onDelete={handleDeleteArticle}
            onCloseEditor={() => setActiveArticle(null)}
            onStartProcessing={handleStartProcessing}
          />
        </div>
        <div className="hidden md:block md:col-span-3 h-full overflow-hidden">
          <TitlePanel
            channel={selectedChannel}
            titles={titles}
            loading={loadingTitles}
            onGenerate={handleGenerateTitles}
            onUpdate={handleUpdateTitle}
            onDelete={handleDeleteTitle}
            onUseTitle={handleStartArticleFromTitle}
          />
        </div>
      </div>

      <PromptSettingsDialog open={promptOpen} onOpenChange={setPromptOpen} />
      <ApiKeysDialog
        open={apiKeysOpen}
        onOpenChange={(o) => {
          setApiKeysOpen(o);
          if (!o) {
            // Refresh key state on close
            settingsClient
              .get("/settings")
              .then((r) =>
                setHasKeys(r.data.google_api_key_set || r.data.openai_api_key_set),
              )
              .catch(() => {});
          }
        }}
      />

      {scenesArticleId && (
        <ScenesModal
          articleId={scenesArticleId}
          onClose={() => setScenesArticleId(null)}
          onArticleUpdated={handleScenesUpdate}
        />
      )}
    </div>
  );
}
